import { TLiftedFormModel } from '../../form/model/form.model';
import { EMPTY_INPUT, FORM_MODIFIER } from '../../form/model/form.modifier';
import { PIPELINE_TABLE } from './table.pipeline';
import { TASK_COMMON_TABLE } from './table.tasks';
import { TTableInfo, TTableOptions, TTableRowNode, TTableTreeModel } from './table.type';

export const ModifiersZTableModel = {
  /**
   * Create Table Model for list of things of type T
   * @param input T Genric input type array
   * @param options Please check types of option in Typescript
   * @param createModel
   * @returns createTableModel<T>
   */
  createTableModel: <T>(
    input: T[],
    options: TTableOptions<T>,
    createModel: (elm: T) => TTableRowNode<T>,
  ): TTableTreeModel<T> => {
    return PIPELINE_TABLE.applyOptions({
      tableInfo: {
        header: {
          type: 'HEADER_ROW',
          cells: [...options.headerConfig],
        },
        rows: input.map<TTableRowNode<T>>((v) => {
          const treeModel = createModel(v);
          return {
            ...treeModel,
            cells: [...treeModel.cells],
          };
        }),
      },
      options: {
        ...options,
        ...(options.edit && {
          edit: {
            ...options.edit,
            isMulti: options.edit.isEditOnly || options.edit.isMulti, // If isEdityOnly by deault row will have mult add suuport
          },
        }),
      },
    });
  },

  /**
   * Update Table model directly
   * @deprecated Please Avoid using this
   * @param tableModel current table model
   * @returns TTableTreeModel<T>
   */
  updateTableModel: <T>(tableModel: TTableTreeModel<T>) => PIPELINE_TABLE.updateAllSelectCells(tableModel),

  /**
   * [SELECT] Toogle row selection by index
   * @param tableModel
   * @param index
   * @returns TTableTreeModel<T>
   */
  selectRowByIndex: <T>(tableModel: TTableTreeModel<T>, index: number) => {
    const { header, rows } = tableModel.tableInfo;
    const isMultiSelect = !!header.cells.find((v) => v.type === 'SELECT_CELL' && v.isMulti);
    return PIPELINE_TABLE.updateAllSelectCells({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: rows.map((v, rowIndex) => {
          // NOTE: If disabled don't apply any changes
          if (v.rowSelectionInfo?.isDisabled) return v;
          const hasSameIndex = index === rowIndex;
          let isSelected = false;
          if (isMultiSelect) {
            isSelected = hasSameIndex ? !v.rowSelectionInfo?.isSelected : !!v.rowSelectionInfo?.isSelected;
          } else {
            isSelected = hasSameIndex ? !v.rowSelectionInfo?.isSelected : false;
          }
          return {
            ...v,
            rowSelectionInfo: {
              isSelected,
              isDisabled: v.rowSelectionInfo?.isDisabled || false,
            },
          };
        }),
      },
    });
  },

  /**
   * [Deselect] Use this when you want to remove the selection from every row in the table.
   * Example: Can be used when implementing "Clear Selection" functionality.
   * @param tableModel
   * @returns TTableTreeModel<T>
   */
  deselectAllRows: <T>(tableModel: TTableTreeModel<T>) => {
    const { rows } = tableModel.tableInfo;
    return PIPELINE_TABLE.updateAllSelectCells({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: rows.map((v) => {
          if (v.rowSelectionInfo?.isDisabled) return v;
          return {
            ...v,
            rowSelectionInfo: {
              isSelected: false,
              isDisabled: v.rowSelectionInfo?.isDisabled || false,
            },
          };
        }),
      },
    });
  },

  /**
   * [Select] Toogle selection of rows from table Head
   * @param tableModel
   * @returns TTableTreeModel<T>
   */
  selectAllRows: <T>(tableModel: TTableTreeModel<T>) => {
    const { header, rows } = tableModel.tableInfo;
    const isHeaderChecked = !!header.cells.find((v) => v.type === 'SELECT_CELL' && v.isChecked);
    const isMultiSelect = !!header.cells.find((v) => v.type === 'SELECT_CELL' && v.isMulti);

    return PIPELINE_TABLE.updateAllSelectCells({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: rows.map((v) => {
          if (v.rowSelectionInfo?.isDisabled) return v;
          return {
            ...v,
            rowSelectionInfo: {
              isSelected: isMultiSelect ? !isHeaderChecked : false,
              isDisabled: v.rowSelectionInfo?.isDisabled || false,
            },
          };
        }),
      },
    });
  },

  /**
   * Add new Row at the top of table ONCE ONLY
   * @param tableModel tableModel
   * @returns updatedTableModel
   */
  addRow: <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
    const { header, rows } = tableModel.tableInfo;
    const isMultiAdd = tableModel.options?.edit?.isMulti || false;
    const isNotMultiAdd = !isMultiAdd;
    const isEditOnly = tableModel.options?.edit?.isEditOnly || false;
    const isNotEditOnly = !isEditOnly;

    const hasAddRow = rows.find((v) => v.type === 'ADD_ROW');
    if (isNotMultiAdd && hasAddRow && isNotEditOnly) return tableModel;

    if (isNotMultiAdd && isNotEditOnly) {
      tableModel = ModifiersZTableModel.closeEditRow(tableModel);
    }

    const editableRow: TTableTreeModel<T>['tableInfo']['header']['cells'] = header.cells.map((v) => {
      switch (v.type) {
        case 'EDIT_ROW': {
          return { ...v };
        }
        default:
          return v.liftedInput
            ? {
                ...v,
              }
            : {
                type: 'EMPTY_CELL',
              };
      }
    });
    const liftedInput = editableRow.map((v) => (v.liftedInput ? v.liftedInput : EMPTY_INPUT));
    return PIPELINE_TABLE.addRowOperation({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: [
          {
            type: 'ADD_ROW',
            formModel: FORM_MODIFIER.createEmptyForm(liftedInput),
            cells: editableRow,
            rowItem: rows[0]?.rowItem,
          },
          ...tableModel.tableInfo.rows,
        ],
      },
    });
  },

  /**
   * [EDIT] Put a Table Row in Edit mode
   * @param tableModel Current Table Model
   * @param index Row Index
   * @returns TTableTreeModel<T>
   */
  editRowByIndex: <T>(tableModel: TTableTreeModel<T>, index?: number): TTableTreeModel<T> => {
    const { header, rows } = tableModel.tableInfo;
    const isMultiEdit = tableModel.options?.edit?.isMulti || false;
    const isNotMultiEdit = !isMultiEdit;

    const updaedRows: TTableInfo<T>['rows'] = rows.map((row, idx) => {
      const inputs = row.cells.map((r, cellIndex) => {
        const lifedInput = header.cells[cellIndex].liftedInput || EMPTY_INPUT;
        return lifedInput;
      });
      switch (row.type) {
        case 'EDIT_ROW': {
          const { formModel, ...rest } = row;
          return isNotMultiEdit ? { ...rest, type: 'ROW' } : { ...row };
        }
        case 'ROW': {
          return index === undefined || index === idx
            ? {
                ...row,
                type: 'EDIT_ROW',
                formModel: row.rowItem
                  ? FORM_MODIFIER.createFromWithData<T>(row.rowItem, inputs)
                  : FORM_MODIFIER.createEmptyForm<T>(inputs),
              }
            : { ...row };
        }
        default: {
          return {
            ...row,
          };
        }
      }
    });
    return PIPELINE_TABLE.editRowOperation({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updaedRows,
      },
    });
  },

  /**
   * [EDIT] Close Edit Row
   * @param tableModel Current Table Model
   * @param index Row Index
   * @returns TTableTreeModel<T>
   */
  closeEditRow: <T>(tableModel: TTableTreeModel<T>, rowIndex?: number): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const isMultiEdit = tableModel.options?.edit?.isMulti || false;
    const isNotMultiEdit = !isMultiEdit;

    const updaedEditRowCell: TTableInfo<T>['rows'] = rows.map((v, idx) => {
      // If row index present don't do
      if (isNotMultiEdit && rowIndex !== undefined && rowIndex !== idx) return v;
      // Remove formModel from row
      switch (v.type) {
        case 'EDIT_ROW': {
          const { formModel, ...rest } = v;
          return rowIndex === idx || rowIndex === undefined
            ? {
                ...rest,
                type: 'ROW',
              }
            : { ...v };
        }
        default: {
          return { ...v };
        }
      }
    });
    return PIPELINE_TABLE.closeEditRowOperation({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updaedEditRowCell,
      },
    });
  },

  /**
   * Remove Row by Index
   * @param tableModel
   * @param index Row Index
   * @returns updatedTableModel
   */
  removeRow: <T>(tableModel: TTableTreeModel<T>, index: number): TTableTreeModel<T> => {
    return PIPELINE_TABLE.closeEditRowOperation({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: tableModel.tableInfo.rows.filter((v, i) => i !== index),
      },
    });
  },

  /**
   * [EDIT] Update Row form model in Edit Mode
   * @param tableModel Current Table Model
   * @param rowIndex Row index which needs to be updates
   * @param formModel updated form model
   * @returns TTableTreeModel<T>
   */
  updateFormModelByIndex: <T>(
    tableModel: TTableTreeModel<T>,
    rowIndex: number,
    formModel: TLiftedFormModel<T>,
  ): TTableTreeModel<T> => {
    const { rows } = tableModel.tableInfo;
    const updatedRows = rows.map((v, i) => (i === rowIndex ? { ...v, formModel } : v));
    return TASK_COMMON_TABLE.logTableModel({
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: updatedRows,
      },
    });
  },

  sortTableByColumnIndex: <T>(tableModel: TTableTreeModel<T>, colIndex: number): TTableTreeModel<T> => {
    const { header, rows } = tableModel.tableInfo;
    const currentOrder = header.cells[colIndex].sortOrder;
    const { order, comp } =
      currentOrder === 'DESC' || currentOrder === 'DEFAULT_ORDER'
        ? { order: 'ASC', comp: 1 }
        : { order: 'DESC', comp: -1 };

    const updatedHeader = {
      ...header,
      cells: header.cells.map((cell, index) =>
        index === colIndex ? { ...cell, sortOrder: order as TTableInfo['header']['cells'][0]['sortOrder'] } : cell,
      ),
    };

    const updatedRows = [...rows].sort((a, b) => {
      const a1 = a.cells[colIndex];
      const b1 = b.cells[colIndex];

      if (a1.type === 'TEXT_CELL' && b1.type === 'TEXT_CELL') {
        const a1Val = Number(a1.text);
        const b1Val = Number(b1.text);
        if (!(isNaN(a1Val) || isNaN(b1Val))) return comp * (b1Val - a1Val);
        return comp * b1.text.toString().localeCompare(a1.text.toString());
      }
      return 0;
    });

    return { ...tableModel, tableInfo: { ...tableModel.tableInfo, header: updatedHeader, rows: updatedRows } };
  },
  updateImageVisibility: <T>(
    tableModel: TTableTreeModel<T>,
    cellLocation: { rowIndex: number; cellIndex: number },
    areImagesHidden: boolean,
  ): TTableTreeModel<T> => {
    const rowsToUpdated = tableModel.tableInfo.rows;
    const { rowIndex, cellIndex } = cellLocation;
    const imageCell = rowsToUpdated[rowIndex].cells[cellIndex];
    if (imageCell.type !== 'IMAGE_CELL') return tableModel;

    rowsToUpdated[rowIndex].cells[cellIndex] = { ...imageCell, areImagesHidden };
    return {
      ...tableModel,
      tableInfo: {
        ...tableModel.tableInfo,
        rows: rowsToUpdated,
      },
    };
  },
};
